import { showErrorData, showSuccessData } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

type Props = {
  lastReviewNote?: string
  lastReviewedAt?: string
  publishStatus?: 'draft' | 'published'
  variant?: 'inline' | 'icon'
  className?: string
}

function formatReviewTime(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function ReviewNoteStatus({
  lastReviewNote,
  lastReviewedAt,
  publishStatus = 'draft',
  variant = 'inline',
  className,
}: Props) {
  const timeText = formatReviewTime(lastReviewedAt)
  const noteText = (lastReviewNote ?? '').trim()
  const displayText =
    timeText && noteText ? `${timeText} · ${noteText}` : timeText || noteText

  if (!displayText) return null

  const isPublished = publishStatus === 'published'
  // 根据发布状态决定圆点颜色：已发布为绿色，草稿(通常是打回)为琥珀色
  const dotClass = isPublished
    ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]'
    : 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]'

  const handleCopy = async () => {
    if (!noteText) return
    try {
      await navigator.clipboard.writeText(noteText)
      showSuccessData('已复制备注')
    } catch (error) {
      showErrorData('复制失败', error)
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {variant === 'icon' ? (
          <Button
            type='button'
            variant='secondary'
            className={cn(
              'group bg-primary/5 text-primary hover:bg-primary/10 border-primary/20 hover:border-primary/40 h-7 rounded-md border px-2 text-[11px] font-semibold transition-all',
              className
            )}
          >
            <div className='flex items-center gap-1.5'>
              <span
                className={cn(
                  'h-1 w-1 shrink-0 rounded-full shadow-sm transition-transform group-hover:scale-125',
                  dotClass
                )}
              />
              <span className='tracking-tight'>审核意见</span>
            </div>
          </Button>
        ) : (
          <button
            type='button'
            title={displayText}
            className={cn(
              'text-muted-foreground hover:text-foreground hover:bg-muted/30 flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 text-xs transition',
              className
            )}
          >
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotClass)}
            />
            <span className='shrink-0 opacity-80'>审核备注</span>
            <span className='truncate font-medium'>{displayText}</span>
          </button>
        )}
      </DialogTrigger>
      <DialogContent className={cn('sm:max-w-lg')}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <span className={cn('h-2.5 w-2.5 rounded-full', dotClass)} />
            审核意见反馈
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4 pt-2 text-sm'>
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-1.5'>
              <div className='text-muted-foreground text-[10px] font-bold tracking-wider uppercase'>
                审核结果
              </div>
              <div
                className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  isPublished
                    ? 'border border-emerald-200/50 bg-emerald-50 text-emerald-700'
                    : 'border border-amber-200/50 bg-amber-50 text-amber-700'
                )}
              >
                {isPublished ? '审核通过' : '审核驳回'}
              </div>
            </div>
            <div className='space-y-1.5 blur-none'>
              <div className='text-muted-foreground text-[10px] font-bold tracking-wider uppercase'>
                审核时间
              </div>
              <div className='font-medium'>{timeText || '—'}</div>
            </div>
          </div>
          <div className='space-y-1.5'>
            <div className='text-muted-foreground text-[10px] font-bold tracking-wider uppercase'>
              反馈备注
            </div>
            <div className='bg-muted/30 text-foreground/80 max-h-80 overflow-y-auto rounded-lg border p-3 leading-relaxed break-words whitespace-pre-wrap'>
              {noteText || '暂无详细备注'}
            </div>
          </div>
        </div>
        <DialogFooter className='sm:justify-end'>
          <Button
            variant='secondary'
            size='sm'
            onClick={handleCopy}
            disabled={!noteText}
          >
            复制备注内容
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
